import { buildRoute } from '@direct-frontend/modules-router';
import { type PreviewId } from '../previewWidget';

export type BundleParams = {
    bundleId: string;
};

export type GiftParams = {
    giftId: string;
};

export type DiscountLadderParams = {
    discountLadderId: string;
};

export type PriceStrategyParams = {
    priceStrategyId: string;
};

export type PromoactionParams = {
    promoactionIdOrType: string;
    promoactionMode: 'promocode' | 'automatic' | 'giftcard';
};

export const PROMOACTIONS_SEARCH_PARAM_ACTIVE_TAB = 'active-tab' as const;
export const PROMOACTIONS_SEARCH_PARAM_PAGE = 'page' as const;
export const PROMOACTIONS_SEARCH_PARAM_PER_PAGE = 'perPage' as const;
export const PROMOACTIONS_SEARCH_PARAM_SORT_BY = 'sortBy' as const;
export const PROMOACTIONS_SEARCH_PARAM_SORT_ORDER = 'sortOrder' as const;
export const PROMOACTIONS_SEARCH_PARAM_STATUS = 'status' as const;
export const PROMOACTIONS_SEARCH_PARAM_PREVIEW = 'preview' as const;
export const PROMOACTIONS_SEARCH_PARAM_FLAGS = 'flags' as const;

export type PromoactionsSearchParams = {
    [PROMOACTIONS_SEARCH_PARAM_FLAGS]?: string;
    [PROMOACTIONS_SEARCH_PARAM_ACTIVE_TAB]?: string;
    [PROMOACTIONS_SEARCH_PARAM_PAGE]?: number;
    [PROMOACTIONS_SEARCH_PARAM_PER_PAGE]?: number;
    [PROMOACTIONS_SEARCH_PARAM_SORT_BY]?: string;
    [PROMOACTIONS_SEARCH_PARAM_SORT_ORDER]?: string;
    [PROMOACTIONS_SEARCH_PARAM_STATUS]?: string;
    [PROMOACTIONS_SEARCH_PARAM_PREVIEW]?: PreviewId;
};

export const promoactionsRoute = buildRoute<never, PromoactionsSearchParams>('/discounts');
export const promoactionRoute = buildRoute<PromoactionParams>('/discount/:promoactionMode/:promoactionIdOrType');
export const promoactionGiftCardFormRoute = buildRoute('/discounts/giftcard');
export const promoactionBundleRoute = buildRoute<BundleParams>('/discounts/bundle/:bundleId');

export const GIFT_ID_NEW = 'new';
export const PRICE_STRATEGY_ID_NEW = 'new';
export const DISCOUNT_LADDER_ID_NEW = 'new';
export const PROMOACTIONS_ACTIVE_TAB_PRICE_STRATEGY = 'price_strategy' as const;
export const promoactionGiftsRoute = buildRoute<GiftParams>('/discounts/gift/:giftId');
export const promoactionDiscountLadderRoute = buildRoute<DiscountLadderParams>(
    '/discounts/discount-ladder/:discountLadderId'
);
export const promoactionPriceStrategyRoute = buildRoute<PriceStrategyParams>(
    '/discounts/price-strategy/:priceStrategyId'
);

export type PromoRecommendationFormParams = {
    recommendationId: string;
};

export const PROMO_RECOMMENDATION_SEARCH_PARAM_DISCOUNT_ID = 'discountId' as const;

export type PromoRecommendationFormSearchParams = {
    [PROMO_RECOMMENDATION_SEARCH_PARAM_DISCOUNT_ID]?: string;
};

export const promoRecommendationFormRoute = buildRoute<
    PromoRecommendationFormParams,
    PromoRecommendationFormSearchParams
>('/discounts/recommendation/:recommendationId');

/**
 * Ссылка на страницу редактирования акции. Три ветки в порядке приоритета:
 *
 * 1. Скидка создана из промо-рекомендации (есть `promoOfferId`) → форма рекомендации
 *    `/discounts/recommendation/:recommendationId?discountId=` (recommendationId = `promoOfferId`, discountId = `id`),
 *    а не обычная страница скидки.
 * 2. Стратегия ценообразования (`kind === 'PRICE_INDEX_AUTO'`) → `/discounts/price-strategy/:id`.
 * 3. Обычная акция (по умолчанию) → `/discount/:promoactionMode/:id` (mode: `automatic` | `promocode` | `giftcard`).
 *
 * @kind helper
 * @returns Относительный URL страницы редактирования акции.
 */
export const getPromoactionEditUrl = ({
    id,
    promoactionMode,
    kind,
    promoOfferId,
}: {
    id: string;
    promoactionMode: PromoactionParams['promoactionMode'];
    kind?: string;
    promoOfferId?: string;
}): string => {
    if (promoOfferId) {
        return promoRecommendationFormRoute.getUrl({
            params: { recommendationId: promoOfferId },
            searchParams: { [PROMO_RECOMMENDATION_SEARCH_PARAM_DISCOUNT_ID]: id },
        });
    }

    if (kind === 'PRICE_INDEX_AUTO') {
        return promoactionPriceStrategyRoute.getUrl({ params: { priceStrategyId: id } });
    }

    return promoactionRoute.getUrl({ params: { promoactionIdOrType: id, promoactionMode } });
};
